import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {
  XMarkIcon,
  BuildingStorefrontIcon,
  UserIcon,
  PhoneIcon,
  IdentificationIcon,
  MapPinIcon,
  ReceiptPercentIcon,
  CheckIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { crearProveedor } from '../utils/proveedores';

const INITIAL_FORM = {
  nombre: '',
  nombre_vendedor: '',
  telefono: '',
  cuit: '',
  direccion: '',
  descuento_efectivo: '',
  cuotas_maximas: '',
  vincular_marca_actual: true,
};

const NuevoProveedorRapidoModal = ({
  isOpen,
  onClose,
  onProveedorCreado,
  proveedoresExistentes = [],
  marcaActualNombre = '',
  marcasCatalogo = [],
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [guardando, setGuardando] = useState(false);

  // Buscar si la marca escrita en el armazón existe en el catálogo con ID numérico
  const marcaCatalogoMatch = React.useMemo(() => {
    const limpia = (marcaActualNombre || '').trim().toLowerCase();
    if (!limpia || !Array.isArray(marcasCatalogo)) return null;
    return (
      marcasCatalogo.find(
        (m) => m && m.id && (m.nombre || '').trim().toLowerCase() === limpia
      ) || null
    );
  }, [marcaActualNombre, marcasCatalogo]);

  useEffect(() => {
    if (isOpen) {
      setForm({
        ...INITIAL_FORM,
        vincular_marca_actual: Boolean(marcaCatalogoMatch),
      });
    }
  }, [isOpen, marcaCatalogoMatch]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const nombreLimpio = (form.nombre || '').trim();
    if (!nombreLimpio) {
      Swal.fire({
        icon: 'warning',
        title: 'Nombre requerido',
        text: 'Ingresá al menos el nombre del proveedor para continuar.',
        confirmButtonColor: '#059669',
      });
      return;
    }

    // Verificar si ya existe en la lista local
    const existente = (proveedoresExistentes || []).find(
      (p) => (p.nombre || '').trim().toLowerCase() === nombreLimpio.toLowerCase()
    );
    if (existente) {
      Swal.fire({
        icon: 'info',
        title: 'Proveedor existente',
        text: `El proveedor "${existente.nombre}" ya estaba registrado y fue seleccionado.`,
        timer: 1600,
        showConfirmButton: false,
      });
      onProveedorCreado?.(existente);
      onClose?.();
      return;
    }

    setGuardando(true);
    try {
      const descuentoVal =
        form.descuento_efectivo !== '' && !Number.isNaN(parseFloat(form.descuento_efectivo))
          ? parseFloat(form.descuento_efectivo)
          : null;
      const cuotasVal =
        form.cuotas_maximas !== '' && !Number.isNaN(parseInt(form.cuotas_maximas, 10))
          ? parseInt(form.cuotas_maximas, 10)
          : null;

      const marcasIds =
        form.vincular_marca_actual && marcaCatalogoMatch?.id
          ? [Number(marcaCatalogoMatch.id)]
          : [];

      const payload = {
        nombre: nombreLimpio,
        nombre_vendedor: (form.nombre_vendedor || '').trim(),
        telefono: (form.telefono || '').trim(),
        cuit: (form.cuit || '').trim(),
        direccion: (form.direccion || '').trim(),
        descuento_efectivo: descuentoVal,
        cuotas_maximas: cuotasVal,
        marcas: marcasIds.length > 0 ? marcasIds : undefined,
      };

      const creado = await crearProveedor(payload);

      Swal.fire({
        icon: 'success',
        title: 'Proveedor creado',
        text: `"${nombreLimpio}" se guardó y seleccionó en el armazón.`,
        timer: 1500,
        showConfirmButton: false,
      });

      onProveedorCreado?.({
        id: creado?.id,
        nombre: nombreLimpio,
        ...payload,
      });
      onClose?.();
    } catch (error) {
      console.error('Error al crear proveedor:', error);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo crear el proveedor',
        text: error.message || 'Ocurrió un error al guardar el proveedor.',
        confirmButtonColor: '#059669',
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1060] flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain"
      onClick={(e) => {
        if (e.target === e.currentTarget && !guardando) {
          onClose?.();
        }
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-lg my-auto max-h-[90vh] flex flex-col border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Encabezado fijo */}
        <div className="shrink-0 bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/15 rounded-xl">
              <BuildingStorefrontIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold leading-tight">
                Nuevo Proveedor
              </h3>
              <p className="text-xs text-emerald-100">
                Solo el nombre es obligatorio; completá el resto si lo tenés a mano
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            title="Cerrar"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Cuerpo scrolleable */}
        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto flex-1 overscroll-contain p-5 space-y-4"
        >
          {/* Nombre del Proveedor (Obligatorio) */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-700 mb-1">
              Nombre del Proveedor / Distribuidora <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <BuildingStorefrontIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                placeholder="Ej: Cacic, De Rigo, Luxottica, Swarovski..."
                autoFocus
                required
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
          </div>

          {/* Grilla Contacto: Vendedor y Teléfono */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Vendedor / Contacto <span className="text-slate-400">(opcional)</span>
              </label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  name="nombre_vendedor"
                  value={form.nombre_vendedor}
                  onChange={handleChange}
                  placeholder="Ej: Martín Gómez"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Teléfono / WhatsApp <span className="text-slate-400">(opcional)</span>
              </label>
              <div className="relative">
                <PhoneIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  name="telefono"
                  value={form.telefono}
                  onChange={handleChange}
                  placeholder="Ej: 11 4567-8901"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Grilla Fiscal y Ubicación: CUIT y Dirección */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                CUIT <span className="text-slate-400">(opcional)</span>
              </label>
              <div className="relative">
                <IdentificationIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  name="cuit"
                  value={form.cuit}
                  onChange={handleChange}
                  placeholder="Ej: 30-71234567-9"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Dirección / Localidad <span className="text-slate-400">(opcional)</span>
              </label>
              <div className="relative">
                <MapPinIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  name="direccion"
                  value={form.direccion}
                  onChange={handleChange}
                  placeholder="Ej: Av. Corrientes 1234, CABA"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Condiciones comerciales en presupuestos */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
              <ReceiptPercentIcon className="w-4 h-4 text-emerald-600" />
              <span>Condiciones Comerciales para Presupuestos (opcional)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  % Descuento Efectivo Especial
                </label>
                <input
                  type="number"
                  name="descuento_efectivo"
                  value={form.descuento_efectivo}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.5"
                  placeholder="Usar general de la óptica"
                  className="w-full px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Tope Cuotas Sin Interés
                </label>
                <input
                  type="number"
                  name="cuotas_maximas"
                  value={form.cuotas_maximas}
                  onChange={handleChange}
                  min="1"
                  max="24"
                  step="1"
                  placeholder="Sin tope especial (ej: 3)"
                  className="w-full px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Vincular marca actual si ya fue seleccionada en el armazón */}
          {marcaCatalogoMatch && (
            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-emerald-50/70 border border-emerald-200 cursor-pointer">
              <input
                type="checkbox"
                name="vincular_marca_actual"
                checked={form.vincular_marca_actual}
                onChange={handleChange}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <div className="flex items-center gap-1.5 text-xs text-emerald-900">
                <TagIcon className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>
                  Vincular automáticamente la marca{' '}
                  <strong className="font-bold">{marcaCatalogoMatch.nombre}</strong> a este proveedor
                </span>
              </div>
            </label>
          )}

          {/* Pie de acciones */}
          <div className="pt-2 border-t border-slate-200 flex flex-col-reverse sm:flex-row items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={guardando}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-xl shadow-sm transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
              <span>{guardando ? 'Guardando...' : 'Crear y Seleccionar'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NuevoProveedorRapidoModal;

